import { useCallback, useContext, useEffect, useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { GridColumn as Column, Grid, GridPageChangeEvent } from '@progress/kendo-react-grid'
import AddButton from 'components/AddButton/AddButton'
import { Pager } from 'components/components/Pager'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { Link } from 'components/Link'
import { useGridState } from 'features/DataGrid'
import { useAppDispatch, useAppSelector } from 'store'
import {
  CompositeFilterDescriptor,
  GridFilterActionState,
  resetGridFilters,
  selectGridFilters,
  setGridFilters,
} from 'store/grid'
import { fetchFilterAction, fetchTickmillCompaniesAction } from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { buildQuery } from 'utils/QueryHelpers'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { NotificationDto } from '../../../api/Clients/Types/NotificationDto'
import { PermissionsEnum } from '../../../api/Identity/Types/PermissionsEnum'
import {
  getNotificationAudienceTypes,
  getNotificationBaseUrlTypes,
  getNotificationPlatforms,
  getNotificationTypes,
} from '../../../store/clientarea/clientarea.actions'
import { fetchClientLanguagesAction } from '../../../store/clients/clients.actions'
import { ClientAreaApiClient } from '../../../utils/api/clientAreaApi'
import { ClientsApiClient } from '../../../utils/api/clientsApi'
import { AuthSessionContext } from '../../../utils/AuthContext'
import { hasPermission } from '../../../utils/permissionsValidator'
import { marketingNotificationsColumns } from './columns'
import { getNotificationFilterFields } from './notificationFilterFields'

const useMapStateToProps = () => ({
  filters: useAppSelector((state) => state.identity.filter),
  gridState: useAppSelector((state) => state.grid),
  hasFilters: useAppSelector(selectGridFilters),
  countries: useAppSelector((state) => state.identity.countries),
  notificationAudienceTypes: useAppSelector((state) => state.clientArea.notificationAudienceTypes),
  notificationTypes: useAppSelector((state) => state.clientArea.notificationTypes),
  notificationPlatforms: useAppSelector((state) => state.clientArea.notificationPlatforms),
  baseUrlTypes: useAppSelector((state) => state.clientArea.baseUrlTypes),
  navigate: useNavigate(),
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  languages: useAppSelector((state) => state.clients.language),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()
  const identityApiClient = useApiClient(IdentityApiClient)

  return {
    fetchFilters: async () => dispatch(fetchFilterAction(identityApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
  }
}

const MarketingNotificationsOverviewPage = () => {
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)
  const navigate = useNavigate()
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const { gridState, gridActions } = useGridState()
  const [data, setData] = useState<NotificationDto[]>()
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const { filters } = useMapStateToProps()
  const mapStateToProps = useMapStateToProps()
  const { fetchFilters, resetGridFilters } = useMapDispatchToProps()
  const dispatch = useAppDispatch()
  const {
    notificationTypes,
    notificationAudienceTypes,
    notificationPlatforms,
    tickmillCompanies,
    languages,
    baseUrlTypes,
  } = mapStateToProps

  useEffect(() => {
    resetGridFilters()
    if (isEmpty(filters)) fetchFilters()
    if (isEmpty(notificationAudienceTypes))
      dispatch(getNotificationAudienceTypes(clientAreaApiClient))
    if (isEmpty(notificationTypes)) dispatch(getNotificationTypes(clientAreaApiClient))
    if (isEmpty(notificationPlatforms)) dispatch(getNotificationPlatforms(clientAreaApiClient))
    if (isEmpty(baseUrlTypes)) dispatch(getNotificationBaseUrlTypes(clientAreaApiClient))
    if (isEmpty(tickmillCompanies)) dispatch(fetchTickmillCompaniesAction(identityApiClient))
    if (isEmpty(languages)) dispatch(fetchClientLanguagesAction(clientsApiClient))
  }, [])

  useEffect(() => {
    fetchNotifications()
  }, [])

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      if (data === undefined) return null
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter, data]
  )

  const fetchNotifications = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const search = params?.query !== undefined ? params.query : getQuery()
      const pageIndex = gridState.pageIndex ?? 1
      const pageSize = gridState.pageSize ?? 10

      const response = await clientAreaApiClient.getNotifications({
        search,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })

      if (response?.items) {
        setData(response.items)
      } else {
        setData([])
      }

      const total = response?.totalResults ?? 0
      gridActions.setGridFilters({
        take: gridState.take ?? 10,
        skip: gridState.skip ?? 0,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    [
      getQuery,
      gridActions,
      gridState.pageIndex,
      gridState.pageSize,
      gridState.take,
      gridState.skip,
      tableData,
      clientAreaApiClient,
    ]
  )

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      pageSize,
      take: pageSize,
      skip: 0,
    })

    const search = getQuery()
    const response = await clientAreaApiClient.getNotifications({
      search,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    if (response?.items) {
      setData(response.items)
    } else {
      setData([])
    }

    const total = response?.totalResults ?? 0
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total,
    })
    setTableData({ ...tableData, meta: response })
  }

  const handleGridPageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pageIndex = Math.floor(skip / take) + 1

    await gridActions.setGridFilters({
      pageIndex,
      skip,
      take,
      pageSize: take,
    })

    const search = getQuery()
    const response = await clientAreaApiClient.getNotifications({
      search,
      pageIndex,
      pageSize: take,
      skipCount: tableData?.skipCount !== false,
    })

    if (response?.items) {
      setData(response.items)
    } else {
      setData([])
    }

    const total = response?.totalResults ?? 0
    await gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, meta: response })
  }

  const handlePageChange = async (page: number) => {
    const search = getQuery()
    const pageIndex = page
    await gridActions.setGridFilters({
      pageIndex,
    })

    const response = await clientAreaApiClient.getNotifications({
      search,
      pageIndex,
      pageSize: gridState.pageSize ?? 10,
      skipCount: tableData?.skipCount !== false,
    })

    if (response?.items) {
      setData(response.items)
    } else {
      setData([])
    }

    const total = response?.totalResults ?? 0
    await gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, meta: response })
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    const search = getQuery()
    const response = await clientAreaApiClient.getNotifications({
      search,
      pageIndex: gridState.pageIndex ?? 1,
      pageSize: gridState.pageSize ?? 10,
      skipCount,
    })

    if (response?.items) {
      setData(response.items)
    } else {
      setData([])
    }

    const total = response?.totalResults ?? 0
    await gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  return (
    <>
      <Breadcrumbs
        crumbs={[{ title: 'Marketing - Notifications', link: links.marketingNotifications.url }]}
      />
      <SelectFiltersFacade
        setGridFilters={gridActions.setGridFilters}
        filterFetch={fetchNotifications}
        filterType={FilterType.NotificationOverview}
        gridState={gridState}
        filterFields={getNotificationFilterFields()}
      />
      <AddButton
        text='Add Notification'
        disabled={!hasPermission(permissions, PermissionsEnum.CanEditNotifications)}
        onClick={() => {
          navigate(
            generatePath(links.marketingNotifications.detailsUrl as string, {
              id: 'add-new',
            })
          )
        }}
        data-test='add-notification-button'
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <Grid
          {...gridState}
          resizable
          data={data}
          total={gridState.total}
          take={gridState.take}
          skip={gridState.skip}
          pageable
          onPageChange={handleGridPageChange}
          style={{ zIndex: 0 }}
          pager={() => (
            <Pager
              onSkipCountChange={handleSkipCountChange}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              page={gridState.pageIndex || 1}
              pageSize={gridState.pageSize || 10}
              take={gridState.take || 10}
              skip={gridState.skip || 0}
            />
          )}
        >
          <Column
            title='Name'
            width={200}
            cell={(gridCellProps) => {
              if (!gridCellProps.dataItem?.name) {
                return <td />
              }
              if (!hasPermission(permissions, PermissionsEnum.CanViewNotifications)) {
                return <td>{gridCellProps.dataItem.name}</td>
              }
              return (
                <td data-test='name-column'>
                  <Link
                    to={{
                      pathname: generatePath(links.marketingNotifications.detailsUrl as string, {
                        id: gridCellProps.dataItem.id,
                        slug: 'details',
                      }),
                    }}
                  >
                    {gridCellProps.dataItem.name}
                  </Link>
                </td>
              )
            }}
          />
          {marketingNotificationsColumns
            .filter((c) => c.show !== false)
            .map((column, idx) => (
              <Column key={idx} {...column} />
            ))}
        </Grid>
      </TableStateContext.Provider>
    </>
  )
}

export default MarketingNotificationsOverviewPage
